<template>
<div class="contents" v-if="opened">
    <div class="time-header" v-if="rulesLoaded && shouldAskDuration">
        <div>{{$t('time_stage.start_time')}}</div>
        <div>{{$t('time_stage.end_time')}}</div>
    </div>

    <div class="time-selection-columns">
        <div class="from-list scroll-height" v-if="rulesLoaded">

            <div v-if="startTimeSet && shouldAskDuration"
              class="time-list--blurred">
                <div class="time-list--blurred-details">
                  <div class="current-selection">
                    {{ DateService.friendlyTime(selectedStartTime.time) }}
                    <strong v-on:click="unselectStartTime">
                      {{$t('global.change')}}
                    </strong>
                  </div>
                  <div>{{$t('time_stage.end_time_prompt')}}</div>
                </div>

            </div>

            <template v-if="startTimesLoading">
                <list-slot
                        v-for="n in 5"
                        :key="'fromTimePlaceholder' + n"
                        :placeholder="true">
                </list-slot>
            </template>

            <template v-if="!startTimeSet">
              <list-slot v-for="fromSlot in startTimes"
                  :key="fromSlot.uniqueId"
                  :main-text="DateService.friendlyTime(fromSlot.time)"
                  :main-detail="!shouldAskDuration ? getFriendlyEndTime(fromSlot.time) : false"
                  :sub-text="fromSlot.subtext ? $t(`time_stage.${fromSlot.subtext}`) : ''"
                  :offers="getOffersForSlot(fromSlot)"
                  :centralise="!shouldAskDuration"
                  :disabled="fromSlot.isDisabled"
                  :selected="isTimeSelected(fromSlot)"
                  :instant="fromSlot.isInstant"
                  v-on:click="selectStartTime(fromSlot)"
                  v-on:disabledClick="offerAlternativesForSlot(fromSlot)"
                  no-arrow="true">
              </list-slot>
            </template>

        </div>

        <div class="until-list scroll-height" v-if="rulesLoaded && shouldAskDuration">
            <div v-if="!startTimeSet"
                    class="time-list--blurred">
              <div class="time-list--blurred-details">
                {{$t('time_stage.start_time_prompt')}}
              </div>
            </div>

            <template v-if="startTimeSet && endTimesLoading">
                <list-slot
                        v-for="n in 5"
                        :key="'untilPlaceholder' + n"
                        :placeholder="true">
                </list-slot>
            </template>

            <template v-if="startTimeSet">
              <list-slot v-for="untilSlot in endTimes"
                          :key="untilSlot.duration === -1 ? 'untilclose' : untilSlot.uniqueId"
                          :main-text="untilSlot.duration === -1 ? $t('time_stage.until_close') : DateService.friendlyTime(untilSlot.time)"
                          :sub-text="$t(`time_stage.${untilSlot.subtext}`)"
                          :offers="getOffersForSlot(untilSlot)"
                          :disabled="untilSlot.isDisabled"
                          :instant="untilSlot.isInstant"
                          v-on:click="selectEndTime(untilSlot)"
                          v-on:disabledClick="offerAlternativesForSlot(fromSlot)">
              </list-slot>
            </template>
        </div>
    </div>
  </div>
</template>

<script>
  import DateService from '../services/DateService'
  import { TimesProvider, BookingTime } from '../services/TimesProvider'
  import listSlot from './listSlot'
  import RulesProvider from '../services/RulesProvider'
  import PromptService from '../services/PromptService'

  export default {
    name: 'timeStage',
    props: [
      'venue',
      'pax',
      'date',
      'type',
      'value',
      'opened',
      'shouldShowOffers'
    ],
    components: {
      listSlot
    },
    data () {
      return {
        DateService,
        startTimes: [],
        endTimes: [],
        startTimesLoading: true,
        endTimesLoading: false,
        shouldAskDuration: true,
        rulesLoaded: false,
        startTimeSet: false,
        selectedStartTime: null
      }
    },
    methods: {
      async requestFromTimes () {
        this.requestRules()
        this.clearStartTimes()
        this.startTimesLoading = true

        const times = (await this.timesProvider.requestStartTimes()).filter(this.shouldDisplayTime)
        this.startTimes = this.startTimes.concat(times)
        this.startTimesLoading = false

        if (!this.times.filter(time => !time.isDisabled).length) {
          this.showUnavailablePrompt()
        }
      },
      async requestFromTimesBatched () {
        await this.requestRules()
        this.clearStartTimes()
        this.startTimesLoading = true

        let firstBookableTime = this.getFirstBookableTime()
        let endOfDay = DateService.addDaysToTime(1, firstBookableTime)
        firstBookableTime.setMinutes(0)
        let currentTime = firstBookableTime
        const timeToAdd = 3

        while (currentTime && currentTime < endOfDay) {
          const fromTime = new Date(currentTime)

          currentTime = DateService.addHoursToTime(timeToAdd, currentTime)

          let toTime = new Date(currentTime)
          toTime.setMinutes(59)
          toTime.setHours(toTime.getHours() - 1)

          if (fromTime.getHours() >= 19) {
            toTime = null
            currentTime = null
          }

          const startTimeBatch = (await this.timesProvider.requestStartTimes(fromTime, toTime)).filter(this.shouldDisplayTime)
          this.startTimes = this.startTimes.concat(startTimeBatch)
          this.startTimesLoading = false
        }

        if (!this.startTimes.filter(time => !time.isDisabled).length) {
          this.showUnavailablePrompt()
        }
      },
      requestEndTimes () {
        this.clearEndTimes()
        this.endTimesLoading = true

        this.timesProvider.requestEndTimes(this.selectedStartTime)
          .then((endTimes) => {
            this.endTimes = endTimes.filter(this.shouldDisplayTime)
            this.endTimesLoading = false
          })
      },

      requestRules () {
        this.shouldAskDuration = true
        this.rulesLoaded = false

        return this.rulesProvider.requestRules()
          .then((rules) => {
            this.bookingRules = rules
            this.shouldAskDuration = rules.shouldAskDuration
            this.rulesLoaded = true
          })
      },

      clearStartTimes () {
        this.startTimes = []
        this.startTimeSet = false
        this.selectedStartTime = null
      },

      clearEndTimes () {
        this.endTimes = []
      },

      isTimeSelected (startTime) {
        return this.selectedStartTime === startTime
      },

      shouldDisplayTime (time) {
        return !time.isInPast
      },

      unselectStartTime () {
        this.startTimeSet = false
        this.selectedStartTime = null
      },

      selectStartTime (startTime) {
        if (startTime.isDisabled) return false

        this.selectedStartTime = startTime
        this.startTimeSet = true

        if (this.shouldAskDuration) {
          return this.requestEndTimes()
        }

        const defaultDuration = (
          this.bookingRules.noDuration ? -1 : this.bookingRules.maxDuration
        )

        const selectedTimes = new BookingTime(
          this.selectedStartTime,
          defaultDuration
        )

        this.$emit('input', selectedTimes)
      },

      selectEndTime (endTime) {
        const selectedTimes = new BookingTime(
          this.selectedStartTime,
          endTime.duration
        )

        this.$emit('input', selectedTimes)
      },

      getFriendlyEndTime (time) {
        const noDuration = this.bookingRules.noDuration

        if (noDuration) {
          return 'close'
        }

        const duration = this.bookingRules.maxDuration
        const endTime = DateService.addMinutesToTime(duration, time)

        return DateService.friendlyTime({
          hours: endTime.getHours(),
          minutes: endTime.getMinutes()
        })
      },

      showUnavailablePrompt () {
        const type = this.$root.booking.type
        const venue = this.$root.booking.venue
        const date = DateService.friendlyDate(this.$root.booking.date)
        const message = this.$t('time_stage.no_availability_message_for_date', {type: type.name, venue: venue.name, date: date})
        const prompt = PromptService.make()
        prompt.title = this.$t('time_stage.no_availability')
        prompt.closeButtonText = this.$t('global.back')
        prompt.closeButtonMethod = 'restartFlow'
        prompt.message = message
        prompt.shouldShowActionButton = true
        prompt.actionButtonText = this.$t('time_stage.see_alternatives')
        prompt.actionButtonMethod = 'showAlternatives'

        if (venue.isTypeCrossSellable(type)) {
          prompt.shouldShowTertiaryButton = true
          prompt.tertiaryButtonText = this.$t('time_stage.check_alternative_venues')
          prompt.tertiaryButtonAction = 'checkAlternativeVenues'
        }

        this.promptService.showNewPrompt(prompt)
        this.$root.prompt = this.promptService.getActivePrompt()
      },
      offerAlternativesForSlot (slot) {
        const venue = this.$root.booking.venue
        const type = this.$root.booking.type
        const time = DateService.friendlyTime(slot.time)
        const message = this.$t('time_stage.no_availability_message_for_time', {type: type.name, time})
        const prompt = PromptService.make()
        prompt.title = this.$t('time_stage.no_availability')
        prompt.message = message
        prompt.shouldShowActionButton = true
        prompt.actionButtonText = this.$t('time_stage.see_alternatives')

        const defaultDuration = (
          this.bookingRules.noDuration ? -1 : this.bookingRules.maxDuration
        )

        this.$root.booking.times = new BookingTime(slot, defaultDuration)

        if (venue.isTypeCrossSellable(type)) {
          prompt.shouldShowTertiaryButton = true
          prompt.tertiaryButtonText = this.$t('time_stage.check_alternative_venues')
          prompt.tertiaryButtonAction = 'checkAlternativeVenues'
        }

        this.promptService.showNewPrompt(prompt)
        this.$root.prompt = this.promptService.getActivePrompt()
      },
      getOffersForSlot (slot) {
        if (this.shouldShowOffers) {
          return slot.offers
        }

        return []
      },
      getFirstBookableTime () {
        let firstBookableTime = DateService.getStartOfDay(this.date || new Date())
        const openCloseTime = this.getOpenAndCloseTimeForVenue(firstBookableTime, this.venue)

        if (openCloseTime && openCloseTime.open) {
          const [hours, mins] = openCloseTime.open.split(':')
          firstBookableTime.setHours(hours, mins, 0)
        }

        if (this.bookingRules && this.bookingRules.bookingsFrom) {
          const [hours, mins] = this.bookingRules.bookingsFrom.split(':')
          firstBookableTime.setHours(hours, mins, 0)
        }

        return firstBookableTime
      },
      getOpenAndCloseTimeForVenue (date, venue) {
        if (!date || !venue.openingTimes) {
          return null
        }

        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
        const dayName = days[date.getDay()]
        const openingTimes = venue.openingTimes

        return openingTimes[dayName]
      }
    },
    mounted () {
      this.timesProvider = new TimesProvider(this.$root.booking)

      this.timesProvider.setUrlOptions({
        source: this.$root.dmnOptions.from
      })

      this.rulesProvider = new RulesProvider(this.$root.booking)

      this.rulesProvider.setUrlOptions({
        source: this.$root.dmnOptions.from
      })

      this.promptService = new PromptService(this.$root.prompt)
    }
  }
</script>



// WEBPACK FOOTER //
// timeStage.vue?8f0b9fcc